import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';

const contactInfo = [
  {
    icon: EmailOutlinedIcon,
    title: 'Email Us',
    details: ['hello@sleepout.in', 'support@sleepout.in'],
  },
  {
    icon: PhoneOutlinedIcon,
    title: 'Call Us',
    details: ['+91 1800-123-4567', 'Mon-Sat: 9AM-8PM'],
  },
  {
    icon: LocationOnOutlinedIcon,
    title: 'Visit Us',
    details: ['123 Sleep Street', 'Mumbai, MH 400001'],
  },
  {
    icon: AccessTimeOutlinedIcon,
    title: 'Business Hours',
    details: ['Monday - Saturday', '9:00 AM - 8:00 PM IST'],
  },
];

function Contact() {
  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    alert('Thank you for your message! We will get back to you soon.');
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h1" sx={{ mb: 2 }}>
              Get in Touch
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, fontSize: '1.1rem' }}>
              Have a question about our pillows or need help with an order? 
              Our sleep experts are here to help.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Cards */}
      <Container maxWidth="lg" sx={{ mt: -4, mb: 8, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={3}>
          {contactInfo.map((info) => (
            <Grid item xs={12} sm={6} md={3} key={info.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  height: '100%',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                }}
              >
                <info.icon
                  sx={{
                    fontSize: 40,
                    color: 'primary.main',
                    mb: 2,
                  }}
                />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  {info.title}
                </Typography>
                {info.details.map((detail, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.8 }}
                  >
                    {detail}
                  </Typography>
                ))}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Contact Form Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={5}>
              <Typography
                variant="overline"
                sx={{
                  color: 'secondary.dark',
                  letterSpacing: 2,
                  fontWeight: 600,
                }}
              >
                Send Us a Message
              </Typography>
              <Typography variant="h2" sx={{ mt: 1, mb: 3 }}>
                We'd Love to
                <br />
                Hear From You
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Whether you have a question about our products, need help 
                choosing the right pillow, or want to share feedback, we're 
                here for you.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Fill out the form and our team will get back to you within 
                24 hours. For urgent inquiries, please call our customer 
                service hotline.
              </Typography>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper
                component="form"
                onSubmit={handleSubmit}
                elevation={0}
                sx={{
                  p: 4,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      multiline
                      rows={5}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      sx={{ py: 1.5, fontWeight: 600 }}
                    >
                      Send Message
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Preview */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3">
              Frequently Asked Questions
            </Typography>
          </Box>

          {[
            {
              q: 'What is your return policy?',
              a: 'We offer a 100-night risk-free trial. If you\'re not completely satisfied, return your pillow for a full refund.',
            },
            {
              q: 'How long does shipping take?',
              a: 'Standard shipping takes 3-5 business days. Express shipping (1-2 days) is available for an additional fee.',
            },
            {
              q: 'Do you ship internationally?',
              a: 'Currently, we ship within India only. We\'re working on expanding to international markets soon.',
            },
            {
              q: 'How do I care for my pillow?',
              a: 'Most of our pillows have removable, machine-washable covers. Check the care label for specific instructions.',
            },
          ].map((faq, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 3,
                mb: 2,
                border: '1px solid #eee',
                '&:hover': {
                  borderColor: 'primary.light',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {faq.q}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {faq.a}
              </Typography>
            </Paper>
          ))}
        </Container>
      </Box>
    </Box>
  );
}

export default Contact;
